// apps/client/src/components/contacts/TrailRiskFollowUp.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  CheckCircle2,
  CircleAlert,
  MinusCircle,
  Phone,
  Mail,
  Loader2,
  Clock,
  TriangleAlert,
  ExternalLink,
} from 'lucide-react';
import { functions } from '../../lib/firebase';
import type { TrailRiskRefStatus } from '../../hooks/useTrailRiskStatusForRefs';

interface TrailRiskFollowUpProps {
  companyId: string;
  /**
   * The status of THIS row's exact occurrence (loan + event type + period), or
   * undefined when the ref is outside the caller's contact scope so the server
   * deliberately said nothing about it.
   */
  status: TrailRiskRefStatus | undefined;
  /**
   * True when the status fetch itself failed. Rendered as "Status unavailable"
   * rather than as an absent chip: a blank would read as "nothing to do", which
   * is the one thing a retention report must never imply by accident.
   */
  statusUnavailable?: boolean;
  /** Called after a successful log so the page can refetch status + the contact timeline. */
  onChanged: () => void;
}

const chip = (cls: string) =>
  `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${cls}`;

/**
 * Trail-risk follow-up control for a report row, resolved PER OCCURRENCE.
 *
 * The log routes through the SERVER logClientContact with this occurrence's
 * eventId, so it closes exactly the loan-item the user clicked (a loan in arrears
 * in both June and July has two rows and two events; the old per-loan lookup could
 * close June from the July row). logClientContact now REJECTS a stale, foreign or
 * dismissed eventId instead of quietly closing nothing, so an error here means a
 * real mismatch and is shown, not swallowed.
 */
export function TrailRiskFollowUp({
  companyId,
  status,
  statusUnavailable = false,
  onChanged,
}: TrailRiskFollowUpProps) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(false);
  const [email, setEmail] = useState(false);
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (statusUnavailable) {
    return (
      <span className={chip('bg-amber-900/30 border-amber-700/60 text-amber-300')}>
        <TriangleAlert className="h-3.5 w-3.5" /> Status unavailable
      </span>
    );
  }

  if (!status) return null;

  // A drop-off explained by a discharge is ONE incident recorded under two
  // reports; the discharge owns it. Offering a second form here would create a
  // duplicate contact record for the same event.
  if (status.aliasOf === 'loan_discharged') {
    return (
      <span className={chip('bg-gray-800 border-gray-700 text-gray-300')}>
        <MinusCircle className="h-3.5 w-3.5" /> Discharged, followed up under Discharges
        <Link
          to="/reports/discharged-loans"
          className="inline-flex items-center gap-1 font-semibold text-blue-400 hover:text-blue-300"
        >
          Open <ExternalLink className="h-3 w-3" />
        </Link>
      </span>
    );
  }

  if (status.state === 'actioned') {
    return (
      <span className={chip('bg-green-900/40 border-green-700 text-green-300')}>
        <CheckCircle2 className="h-3.5 w-3.5" /> Followed up
      </span>
    );
  }
  if (status.state === 'dismissed') {
    return (
      <span className={chip('bg-gray-800 border-gray-700 text-gray-400')}>
        <MinusCircle className="h-3.5 w-3.5" /> Dismissed
      </span>
    );
  }
  if (status.state === 'before_tracking_floor') {
    return (
      <span className={chip('bg-gray-800 border-gray-700 text-gray-500')}>
        <Clock className="h-3.5 w-3.5" /> Before completion tracking started
      </span>
    );
  }
  if (status.state === 'tracking_not_enabled') {
    return (
      <span className={chip('bg-gray-800 border-gray-700 text-gray-500')}>
        <MinusCircle className="h-3.5 w-3.5" /> Tracking not enabled
      </span>
    );
  }
  if (status.state === 'pending_materialisation') {
    // Above the tracking floor and tracked, but no event was written. Real work may
    // exist that nothing is reminding anyone about, so this is the state that MUST
    // be loud rather than blank.
    return (
      <span
        className={chip('bg-amber-900/30 border-amber-700/60 text-amber-300')}
        title="This alert has not been generated yet. If it persists past the next overnight run, raise it."
      >
        <TriangleAlert className="h-3.5 w-3.5" /> Alert not generated yet
      </span>
    );
  }

  // state === 'open'. An open event always carries its ids; guard anyway so a
  // malformed row shows the unavailable chip instead of posting a null eventId.
  if (!status.eventId || !status.clientId) {
    return (
      <span className={chip('bg-amber-900/30 border-amber-700/60 text-amber-300')}>
        <TriangleAlert className="h-3.5 w-3.5" /> Status unavailable
      </span>
    );
  }

  const submit = async () => {
    if (!phone && !email) {
      setError('Select phone or email.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'logClientContact');
      const res = await fn({
        companyId,
        clientId: status.clientId,
        eventId: status.eventId,
        contactedByPhone: phone,
        contactedByEmail: email,
        comments: comments.trim(),
      });
      const data = (res.data ?? {}) as {
        ok?: boolean;
        eventsClosed?: number;
        alreadyActioned?: boolean;
      };
      // `alreadyActioned` is SUCCESS: someone else completed this occurrence while
      // the page was open. Closing nothing WITHOUT that flag is not — the server
      // returns ok:true for both, and only the former means the work is done, so
      // this must surface rather than look like a save (see logClientContact.ts).
      const closedSomething = (data.eventsClosed ?? 0) > 0;
      if (data.ok === false || (!closedSomething && !data.alreadyActioned)) {
        throw new Error('The follow-up was not recorded. Refresh the page and try again.');
      }
      setOpen(false);
      setPhone(false);
      setEmail(false);
      setComments('');
      onChanged();
    } catch (e) {
      console.error('[TrailRiskFollowUp] logClientContact failed', e);
      setError(e instanceof Error ? e.message : 'Failed to log contact.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inline-block">
      <div className="flex items-center gap-2">
        <span className={chip('bg-amber-900/40 border-amber-700 text-amber-300')}>
          <CircleAlert className="h-3.5 w-3.5" /> Follow-up needed
        </span>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-xs font-semibold text-blue-400 hover:text-blue-300"
          >
            Log contact + complete
          </button>
        )}
      </div>

      {open && (
        <div className="mt-2 w-72 rounded-lg border border-gray-700 bg-gray-900 p-3 shadow-lg">
          <div className="mb-2 flex gap-3">
            <label className="flex items-center gap-1.5 text-xs text-gray-300">
              <input type="checkbox" checked={phone} onChange={(e) => setPhone(e.target.checked)} />
              <Phone className="h-3.5 w-3.5" /> Phone
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-300">
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
              <Mail className="h-3.5 w-3.5" /> Email
            </label>
          </div>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Reason / outcome (e.g. refinanced to Westpac, property sold)"
            rows={2}
            className="mb-2 w-full rounded border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-white placeholder-gray-500"
          />
          {error && <p className="mb-2 text-xs text-red-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
              disabled={saving}
              className="rounded px-2.5 py-1 text-xs text-gray-400 hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Mark followed up
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
